import { useCallback, useEffect, useRef, useState } from 'react';

import { fetchImageUsingCapacitorHTTP } from '../../../../imageFetchUtils';
import { decodeImageUrl } from '../../../content/utils/imageModalImageDecodeUtils';
import { acquireDecodedImage, type DecodedImageHandle } from '../../../content/utils/imageModalDecodedImageCache';

const loadPreviewDisplayUrl = async (sourceUrl: string, imageType: string): Promise<string> => {
    try {
        await decodeImageUrl(sourceUrl);
        return sourceUrl;
    } catch {
        const { objectUrl } = await fetchImageUsingCapacitorHTTP(sourceUrl, imageType);

        try {
            await decodeImageUrl(objectUrl);
            return objectUrl;
        } catch (error) {
            URL.revokeObjectURL(objectUrl);
            throw error;
        }
    }
};

type UseImageModalV2BackgroundPreviewOptions = {
    sourceUrl?: string;
    imageType: string;
    sourceIdentity: string;
    deferImageCommit: boolean;
    onDeferredImageReady: () => void;
    onPreviewReadyChange: (ready: boolean) => void;
};

/**
 * Decodes staged background previews while retaining the painted outgoing image. First-time removal can
 * defer the decoded source commit until the opaque wipe cover is mounted.
 */
export const useImageModalV2BackgroundPreview = ({
    sourceUrl,
    imageType,
    sourceIdentity,
    deferImageCommit,
    onDeferredImageReady,
    onPreviewReadyChange,
}: UseImageModalV2BackgroundPreviewOptions) => {
    const [displayUrl, setDisplayUrl] = useState<string | null>(() => sourceUrl || null);
    const displayUrlRef = useRef<string | null>(displayUrl);
    // The displayed handle owns the object URL the preview <img> is painting; it is only released once a
    // replacement has committed — releasing it when the next variant starts loading would revoke the URL
    // and blank the retained outgoing image mid-toggle. Loading/pending handles cover the in-flight and
    // decoded-but-deferred stages respectively.
    const displayedHandleRef = useRef<DecodedImageHandle | null>(null);
    const loadingHandleRef = useRef<DecodedImageHandle | null>(null);
    const pendingHandleRef = useRef<DecodedImageHandle | null>(null);
    const pendingDisplayUrlRef = useRef<string | null>(null);
    const deferImageCommitRef = useRef(deferImageCommit);
    const loadGenerationRef = useRef(0);
    const onDeferredImageReadyRef = useRef(onDeferredImageReady);
    const onPreviewReadyChangeRef = useRef(onPreviewReadyChange);
    displayUrlRef.current = displayUrl;
    deferImageCommitRef.current = deferImageCommit;
    onDeferredImageReadyRef.current = onDeferredImageReady;
    onPreviewReadyChangeRef.current = onPreviewReadyChange;

    const commitPreview = useCallback((nextDisplayUrl: string, handle: DecodedImageHandle | null) => {
        if (handle) {
            if (loadingHandleRef.current === handle) loadingHandleRef.current = null;
            if (pendingHandleRef.current === handle) pendingHandleRef.current = null;
            if (displayedHandleRef.current !== handle) {
                displayedHandleRef.current?.release();
                displayedHandleRef.current = handle;
            }
        }
        setDisplayUrl(nextDisplayUrl);
        displayUrlRef.current = nextDisplayUrl;
        onPreviewReadyChangeRef.current(true);
    }, []);

    const commitPendingPreview = useCallback(() => {
        const pendingDisplayUrl = pendingDisplayUrlRef.current;
        const pendingHandle = pendingHandleRef.current;
        pendingDisplayUrlRef.current = null;
        if (pendingDisplayUrl) commitPreview(pendingDisplayUrl, pendingHandle);
    }, [commitPreview]);

    useEffect(
        () => () => {
            displayedHandleRef.current?.release();
            displayedHandleRef.current = null;
            loadingHandleRef.current?.release();
            loadingHandleRef.current = null;
            pendingHandleRef.current?.release();
            pendingHandleRef.current = null;
        },
        [],
    );

    useEffect(() => {
        if (!sourceUrl) return undefined;

        let cancelled = false;
        const generation = loadGenerationRef.current + 1;
        loadGenerationRef.current = generation;
        const isCurrentLoad = () => !cancelled && generation === loadGenerationRef.current;

        if (!deferImageCommitRef.current && !displayUrlRef.current) {
            setDisplayUrl(sourceUrl);
            displayUrlRef.current = sourceUrl;
        }

        // Acquire the new handle before releasing any superseded one — if they share the same cache key
        // (an unchanged sourceUrl re-running for another dependency) the ref count never touches zero.
        // Only the not-yet-displayed stages are superseded; the displayed handle waits for commit.
        const nextHandle = acquireDecodedImage(sourceUrl, imageType, () => loadPreviewDisplayUrl(sourceUrl, imageType));
        const supersededLoadingHandle = loadingHandleRef.current;
        loadingHandleRef.current = nextHandle;
        supersededLoadingHandle?.release();
        const supersededPendingHandle = pendingHandleRef.current;
        pendingHandleRef.current = null;
        pendingDisplayUrlRef.current = null;
        supersededPendingHandle?.release();

        void nextHandle.promise
            .then((nextDisplayUrl) => {
                if (!isCurrentLoad()) return;
                if (deferImageCommitRef.current) {
                    pendingDisplayUrlRef.current = nextDisplayUrl;
                    if (loadingHandleRef.current === nextHandle) loadingHandleRef.current = null;
                    pendingHandleRef.current = nextHandle;
                    onDeferredImageReadyRef.current();
                    return;
                }

                pendingDisplayUrlRef.current = null;
                commitPreview(nextDisplayUrl, nextHandle);
            })
            .catch(() => {
                // The decode never reached the displayed/pending stages, so release this load's still-held
                // handle (unless a newer load already replaced it) instead of leaking its cache refcount and
                // object URL until unmount — mirrors useImageModalEditorImageLoader's rejection path.
                if (loadingHandleRef.current === nextHandle) {
                    loadingHandleRef.current = null;
                    nextHandle.release();
                }
                if (isCurrentLoad() && deferImageCommitRef.current) onDeferredImageReadyRef.current();
            });

        return () => {
            cancelled = true;
        };
    }, [commitPreview, imageType, sourceIdentity, sourceUrl]);

    return { displayUrl, commitPendingPreview };
};
